import type { Option } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";
import type { GestureSystem } from "./gesture-system.ts";

/**
 * Base interface for gesture system state.
 * Systems extend this with their specific state information.
 *
 * Each state represents an "active mode" in a gesture system that may need
 * to be dismissed when another system takes precedence.
 */
export interface GestureSystemState {
  /** The gesture system that owns this state */
  readonly source: GestureSystem;

  /**
   * Unique identifier for this state instance.
   * Convention: `{system-name}-{state-kind}` e.g. "all-day-pending-new-event"
   */
  readonly id: string;

  /**
   * Whether this state should block gestures in other systems when dismissed.
   * - `true`: The dismissing gesture is consumed (e.g., pending new event form —
   *   don't accidentally create while dismissing a half-filled form).
   * - `false`: Dismiss and let the gesture proceed (e.g., existing
   *   event details, context menus).
   */
  readonly blocking: boolean;

  /**
   * Dismiss/cancel this state.
   * Called when another system takes precedence or user dismisses.
   * Implementation should clean up UI (hide ghost, close popover, etc).
   */
  dismiss(): void;
}

/**
 * Manages global gesture state across all systems.
 *
 * Systems register their state when entering an "active mode" (e.g., pending new event).
 * Other systems can check for and dismiss states in other systems to coordinate.
 */
export class GlobalGestureStateManager {
  #states: Map<string, GestureSystemState> = new Map();

  registerState(state: GestureSystemState): void {
    Tracing.log("gesture-state/register", { id: state.id, totalStates: this.#states.size + 1 });
    this.#states.set(state.id, state);
  }

  unregisterState(id: string): void {
    Tracing.log("gesture-state/unregister", { id, totalStates: this.#states.size - 1 });
    this.#states.delete(id);
  }

  hasState(id: string): boolean {
    return this.#states.has(id);
  }

  /** Whether ANY state is registered (delegate popovers, pending
   *  forms). Used by empty-column dismissal to decide engagement. */
  hasStates(): boolean {
    return this.#states.size > 0;
  }

  hasStateInOtherSystem(
    source: GestureSystem,
    predicate: Option<(state: GestureSystemState) => boolean>,
  ): boolean {
    for (const state of this.#states.values()) {
      if (state.source === source) continue;
      if (!predicate || predicate(state)) return true;
    }
    return false;
  }

  /**
   * Dismiss every registered state regardless of source. Used by the
   * coordinator's click-outside path: delegate-owned popovers (tasks,
   * scheduling links) register states here instead of owning a
   * GestureSystem, so `closeAllMenus` fan-out alone would miss them.
   */
  dismissAll(): void {
    for (const [id, state] of [...this.#states.entries()]) {
      Tracing.log("gesture-state/dismiss-all", { id });
      state.dismiss();
    }
    this.#states.clear();
  }

  dismissOtherSystems(
    requestingSource: GestureSystem,
    predicate: Option<(state: GestureSystemState) => boolean>,
  ): boolean {
    let dismissed = false;
    const toDelete: string[] = [];
    for (const [id, state] of this.#states.entries()) {
      if (state.source === requestingSource) continue;
      if (predicate && !predicate(state)) continue;
      Tracing.log("gesture-state/dismiss-other", { id });
      state.dismiss();
      toDelete.push(id);
      dismissed = true;
    }
    for (const id of toDelete) this.#states.delete(id);
    return dismissed;
  }
}
